import React, { useState, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './PeriodTracker.css';
import { FaCalendarAlt } from 'react-icons/fa'; // Importing calendar icon

const PeriodTracker = () => {
  const [lastPeriodDate, setLastPeriodDate] = useState(new Date());
  const [periodLength, setPeriodLength] = useState(7);
  const [cycleLength, setCycleLength] = useState(28);
  const [showResult, setShowResult] = useState(false);
  const [currentMonthPhases, setCurrentMonthPhases] = useState([]);
  const [nextMonthPhases, setNextMonthPhases] = useState([]);
  const [isCurrentMonth, setIsCurrentMonth] = useState(false);
  const [flippedIndex, setFlippedIndex] = useState(null);
  const [calendarVisible, setCalendarVisible] = useState(false);
  const resultRef = useRef(null);

  const calculatePhases = (startDate) => {
    const phases = [];
    if (startDate && periodLength && cycleLength) {
      const currentDate = new Date(startDate);
      const prePeriodStart = new Date(currentDate);
      prePeriodStart.setDate(prePeriodStart.getDate() - 5);
      const periodStart = new Date(currentDate);
      const postPeriodStart = new Date(currentDate);
      postPeriodStart.setDate(postPeriodStart.getDate() + periodLength);
      const ovulationStart = new Date(currentDate);
      ovulationStart.setDate(ovulationStart.getDate() + periodLength + 5);

      const prePeriodEnd = new Date(prePeriodStart);
      prePeriodEnd.setDate(prePeriodEnd.getDate() + 4);

      const periodEnd = new Date(periodStart);
      periodEnd.setDate(periodEnd.getDate() + periodLength - 1);

      const postPeriodEnd = new Date(postPeriodStart);
      postPeriodEnd.setDate(postPeriodEnd.getDate() + 4);

      const ovulationEnd = new Date(ovulationStart);
      ovulationEnd.setDate(ovulationEnd.getDate() + 4);

      phases.push({
        name: 'Pre-Period',
        from: prePeriodStart.toDateString().slice(4),
        to: prePeriodEnd.toDateString().slice(4),
        description: 'Pre-Period: This phase occurs just before your period starts. You might experience some PMS symptoms.'
      });
      phases.push({
        name: 'Period Days',
        from: periodStart.toDateString().slice(4),
        to: periodEnd.toDateString().slice(4),
        description: 'Period Days: This is when you have your menstrual bleeding.'
      });
      phases.push({
        name: 'Post Period',
        from: postPeriodStart.toDateString().slice(4),
        to: postPeriodEnd.toDateString().slice(4),
        description: 'Post Period: This phase comes right after your period ends and lasts until ovulation.'
      });
      phases.push({
        name: 'Peak Ovulation',
        from: ovulationStart.toDateString().slice(4),
        to: ovulationEnd.toDateString().slice(4),
        description: 'Peak Ovulation: The phase when you are most fertile. Ovulation occurs around the middle of your cycle.'
      });
    }
    return phases;
  };

  const handleCalculateCurrentMonth = (e) => {
    e.preventDefault();
    setShowResult(true);
    const currentPhases = calculatePhases(lastPeriodDate);
    setCurrentMonthPhases(currentPhases);
    setIsCurrentMonth(true);
  };

  const handlePredictNextMonth = (e) => {
    e.preventDefault();
    const nextPeriod = new Date(lastPeriodDate);
    nextPeriod.setDate(nextPeriod.getDate() + cycleLength);
    const nextPhases = calculatePhases(nextPeriod);
    setNextMonthPhases(nextPhases);
    setShowResult(true);
    setIsCurrentMonth(false);
  };

  const handleReset = () => {
    setLastPeriodDate(new Date());
    setPeriodLength(7);
    setCycleLength(28);
    setShowResult(false);
    setCurrentMonthPhases([]);
    setNextMonthPhases([]);
    setIsCurrentMonth(false);
    setFlippedIndex(null); // Reset flipped index
    
    // Scroll to top
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handlePeriodLengthChange = (delta) => {
    setPeriodLength(prev => Math.max(1, Math.min(10, prev + delta))); // Limit between 1 and 10
  };

  const handleCycleLengthChange = (delta) => {
    setCycleLength(prev => Math.max(15, Math.min(35, prev + delta))); // Limit between 15 and 35
  };

  const handleFlip = (index) => {
    setFlippedIndex(index);
    setTimeout(() => {
      setFlippedIndex(null); // Reset flipped index after 4 seconds
    }, 4000); // Set to 4000ms for 4 seconds
  };

  const handleCalendarVisibility = (visible) => {
    setCalendarVisible(visible);
  };

  useEffect(() => {
    if (showResult && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showResult]);

  return (
    <div className={`container1 ${calendarVisible ? 'move-buttons-down' : ''}`}>
      {/* Falling leaves */}
      <img src="/period-tracker/feather.png" alt="Feather" className="feather" />
      <img src="/period-tracker/tampon.png" alt="Feather" className="feather" />
      <img src="/period-tracker/feather.png" alt="Feather" className="feather" />
     <img src="/period-tracker/tampon.png" alt="Feather" className="feather" />
     <img src="/period-tracker/feather.png" alt="Feather" className="feather" />
     <img src="/period-tracker/tampon.png" alt="Feather" className="feather" />
      <img src="/period-tracker/feather.png" alt="Feather" className="feather" />
      <img src="/period-tracker/tampon.png" alt="Feather" className="feather" />
     <img src="/period-tracker/feather.png" alt="Feather" className="feather" />
     <img src="/period-tracker/tampon.png" alt="Feather" className="feather" />

      <div className="advisory-note">
        Advisory: Not tailored for PCOS/PCOD conditions.
      </div>
      <div className='period-tracker'>
        <h2 className="tracker-title">Period Tracker</h2>
        <form className='tracker-form'>
          <div className='input-module'>
            <label className='input-label'>
              Date of your last period?
              <div className='date-picker-container'>
                <DatePicker
                  selected={lastPeriodDate}
                  onChange={(date) => setLastPeriodDate(date)}
                  dateFormat="dd-MM-yyyy"
                  className="date-picker"
                  calendarClassName="rounded-calendar"
                  onCalendarOpen={() => handleCalendarVisibility(true)}
                  onCalendarClose={() => handleCalendarVisibility(false)}
                />
                <FaCalendarAlt
                  className="calendar-icon"
                  onClick={() => handleCalendarVisibility(!calendarVisible)}
                />
              </div>
            </label>
          </div>
          <div className='input-module'>
            <label className='input-label'>
              How long did it last?
              <div className='input-field-container'>
                <button type='button' className='icon-button minus-button' onClick={() => handlePeriodLengthChange(-1)}>-</button>
                <input
                  type='text'
                  value={periodLength}
                  readOnly
                  className='input-field'
                />
                <button type='button' className='icon-button plus-button' onClick={() => handlePeriodLengthChange(1)}>+</button>
              </div>
            </label>
          </div>
          <div className='input-module'>
            <label className='input-label'>
              What’s your usual cycle length?
              <div className='input-field-container'>
                <button type='button' className='icon-button minus-button' onClick={() => handleCycleLengthChange(-1)}>-</button>
                <input
                  type='text'
                  value={cycleLength}
                  readOnly
                  className='input-field'
                />
                <button type='button' className='icon-button plus-button' onClick={() => handleCycleLengthChange(1)}>+</button>
              </div>
            </label>
          </div>
          <div className="tracker-buttons">
            <button type='button' className='tracker-button' onClick={handleCalculateCurrentMonth}>Calculate Current Month Phases</button>
            <button type='button' className='tracker-button' onClick={handlePredictNextMonth}>Predict Next Month Phases</button>
          </div>
        </form>
        {showResult && (
          <div className='result-container' ref={resultRef}>
            <p className="result-note">Caution: This tracker offers a rough estimate, and individual variations are possible.</p>
            <h3 className="result-title">{isCurrentMonth ? 'Your Cycle Phases for Current Month' : 'Your Cycle Phases for Next Month'}</h3>
            <div className='result'>
              {(isCurrentMonth ? currentMonthPhases : nextMonthPhases).map((phase, index) => (
                <div key={index} className='phase-container'>
                  <div
                    className={`phase ${flippedIndex === index ? 'flipped' : ''}`}
                    onClick={() => handleFlip(index)}
                  >
                    <div className='phase-front'>
                      <h4>{phase.name}</h4>
                      <p>{phase.from} to {phase.to}</p>
                    </div>
                    <div className='phase-back'>
                      <p>{phase.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button type='button' className='tracker-button reset-button' onClick={handleReset}>Reset</button>
            <p className="click-note">Click on the cards to know more.</p> {/* Added note */}
          </div>
        )}
      </div>
    </div>
  );
};

export default PeriodTracker;
